import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Avatar, Button, IconButton, Divider, CircularProgress,
  Checkbox, FormControlLabel, Chip, Tooltip, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import CommentIcon from '@mui/icons-material/Comment';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../utils/ckeditor';
import { commentsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { resolveMentions, highlightMentions } from '../utils/mentions';
import { renderRichHtml, sanitizeRichHtml, looksLikeHtml, toEditorHtml, richHtmlIsEmpty as htmlIsEmpty } from '../utils/sanitizeHtml';

const COMMENT_TOOLBAR = ['bold', 'italic', '|', 'bulletedList', 'numberedList', '|', 'link', '|', 'blockQuote', '|', 'undo', 'redo'];

/** Rich-text comment editor. Uncontrolled (data via key reset) to avoid cursor jumps. */
function CommentEditor({ onChange, placeholder, initialValue = '' }) {
  return (
    <Box sx={{
      flex: 1, border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1,
      '& .ck-editor__editable': { minHeight: '70px' },
      '& .ck-content ul, & .ck-content ol': { paddingLeft: '1.5rem', margin: '0.4em 0' },
      '& .ck-content li': { marginBottom: '0.2em' },
      '& .ck-content p': { margin: '0.4em 0' },
    }}>
      <CKEditor
        editor={ClassicEditor}
        // Normalized: content stored as plain text (e.g. posted via the
        // integrations API) would otherwise collapse into a single paragraph,
        // because CKEditor ignores raw \n and only understands HTML.
        data={toEditorHtml(initialValue)}
        config={{ placeholder: placeholder || 'Kommentar schreiben…', toolbar: COMMENT_TOOLBAR }}
        onChange={(_, editor) => onChange(editor.getData())}
      />
    </Box>
  );
}

/** Checkbox "Intern" – nur für interne Nutzer, nie für Kunden-Accounts. */
function InternalToggle({ checked, onChange, disabled = false }) {
  return (
    <Tooltip title="Interne Notiz: nur für das RedOrbit-Team sichtbar, der Kunde sieht sie nicht.">
      <FormControlLabel
        sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
        control={
          <Checkbox size="small" checked={checked} disabled={disabled}
            onChange={(e) => onChange(e.target.checked)} sx={{ py: 0.25 }} />
        }
        label="Intern (Kunde sieht diesen Kommentar nicht)"
      />
    </Tooltip>
  );
}

/**
 * Zeigt live, wer durch @Name im Text benachrichtigt wird. `notified` sind die
 * beim Bearbeiten bereits angeschriebenen User – die bekommen keine zweite Mail.
 */
function MentionHint({ html, users, notified = [], isInternal = false }) {
  const mentioned = resolveMentions(html, users);
  if (!mentioned.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Mit <strong>@Vorname</strong>, <strong>@Nachname</strong> oder <strong>@Vorname Nachname</strong>{' '}
        jemanden direkt ansprechen – die Person bekommt eine E-Mail mit Link zum Ticket.
      </Typography>
    );
  }
  const already = new Set(notified || []);
  // Kunden sehen interne Notizen nicht – der Server verschickt an sie dann
  // bewusst keine Mail. Das muss die Vorschau zugeben, sonst verspricht sie
  // etwas, das nicht passiert.
  const blocked = (u) => isInternal && (u.is_client || u.role === 'client');
  const anyBlocked = mentioned.some(blocked);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary">Benachrichtigt:</Typography>
      {mentioned.map((u) => {
        const name = u.full_name || u.username;
        if (blocked(u)) {
          return (
            <Chip key={u.id} size="small" variant="outlined" color="default"
              icon={<LockIcon sx={{ fontSize: 12 }} />}
              label={`${name} – keine Mail`}
              sx={{ height: 20, fontSize: '0.7rem', opacity: 0.7 }} />
          );
        }
        return (
          <Chip key={u.id} size="small" variant="outlined"
            color={already.has(u.id) ? 'default' : 'primary'}
            icon={<AlternateEmailIcon sx={{ fontSize: 13 }} />}
            label={already.has(u.id) ? `${name} (bereits)` : name}
            sx={{ height: 20, fontSize: '0.7rem' }} />
        );
      })}
      {anyBlocked && (
        <Typography variant="caption" color="text.secondary">
          – Kunden bekommen zu internen Notizen keine Mail
        </Typography>
      )}
    </Box>
  );
}

/**
 * Wer zu diesem Kommentar tatsächlich eine Mail bekommen hat – aus dem
 * Server-Feld `mentions`, nicht aus dem Text geraten. Fehlt der Name in der
 * geladenen Userliste (z. B. für Kunden-Accounts), wird nur gezählt.
 */
function NotifiedLine({ ids, users }) {
  if (!ids || !ids.length) return null;
  const names = ids.map((id) => users.find((u) => u.id === id))
    .filter(Boolean).map((u) => u.full_name || u.username);
  return (
    <Typography variant="caption" color="text.secondary"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
      <AlternateEmailIcon sx={{ fontSize: 13 }} />
      {names.length
        ? `Benachrichtigt: ${names.join(', ')}`
        : `${ids.length} Person(en) benachrichtigt`}
    </Typography>
  );
}

const initials = (u) => ((u?.full_name || u?.username || '?').charAt(0).toUpperCase());
const fmt = (iso) => {
  try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return ''; }
};

/**
 * Threaded comment section for a task. Works for internal and external (client)
 * users; the backend scopes /api/comments to the user's customer.
 */
function TaskComments({ taskId }) {
  const { user, isAdmin } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [repliesByComment, setRepliesByComment] = useState({});
  const [openReplyFor, setOpenReplyFor] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editorReset, setEditorReset] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  // Interne Notizen: nur für das interne Team (admin/user), nie für Kunden.
  const isClient = !!user?.is_client;
  const [newInternal, setNewInternal] = useState(false);
  const [replyInternal, setReplyInternal] = useState(false);
  const [editingInternal, setEditingInternal] = useState(false);
  const [mentionUsers, setMentionUsers] = useState([]); // für die @-Vorschau
  const [notice, setNotice] = useState(null);          // {severity, text}
  const [publishTarget, setPublishTarget] = useState(null); // Freigabe-Rückfrage

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await commentsAPI.getAll(taskId);
      setComments(res.data || []);
    } catch (err) {
      console.error('[COMMENTS] load error', err);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    usersAPI.getAll()
      .then((res) => { if (!cancelled) setMentionUsers(res.data || []); })
      .catch((err) => console.error('[COMMENTS] user list error', err));
    return () => { cancelled = true; };
  }, []);

  const loadReplies = async (commentId) => {
    try {
      const res = await commentsAPI.getReplies(commentId);
      setRepliesByComment((prev) => ({ ...prev, [commentId]: res.data || [] }));
    } catch (err) {
      console.error('[COMMENTS] replies error', err);
    }
  };

  /** Rückmeldung, wen der Server tatsächlich per @Erwähnung benachrichtigt hat. */
  const announceMentions = (created) => {
    const ids = created?.mentions || [];
    if (!ids.length) return;
    const names = ids
      .map((id) => mentionUsers.find((u) => u.id === id))
      .filter(Boolean)
      .map((u) => u.full_name || u.username);
    setNotice({
      severity: 'success',
      text: names.length
        ? `Per E-Mail benachrichtigt: ${names.join(', ')}`
        : `${ids.length} Person(en) per E-Mail benachrichtigt.`,
    });
  };

  const postComment = async () => {
    if (htmlIsEmpty(newComment)) return;
    setPosting(true);
    try {
      const res = await commentsAPI.create({
        task_id: taskId, content: newComment.trim(), is_internal: newInternal,
      });
      announceMentions(res.data);
      setNewComment('');
      setNewInternal(false);
      setEditorReset((c) => c + 1);
      await load();
    } catch (err) {
      console.error('[COMMENTS] post error', err);
    } finally {
      setPosting(false);
    }
  };

  const postReply = async (parent) => {
    if (htmlIsEmpty(replyText)) return;
    const parentId = parent.id;
    try {
      const res = await commentsAPI.create({
        task_id: taskId,
        content: replyText.trim(),
        parent_id: parentId,
        // Antworten auf interne Notizen sind zwingend intern (das Backend
        // erzwingt es ebenfalls) – sonst würde der Thread halb sichtbar.
        is_internal: parent.is_internal || replyInternal,
      });
      announceMentions(res.data);
      setReplyText('');
      setReplyInternal(false);
      setOpenReplyFor(null);
      await Promise.all([load(), loadReplies(parentId)]);
    } catch (err) {
      console.error('[COMMENTS] reply error', err);
    }
  };

  const remove = async (id, parentId) => {
    try {
      await commentsAPI.delete(id);
      if (parentId) { await Promise.all([load(), loadReplies(parentId)]); }
      else { await load(); }
    } catch (err) {
      console.error('[COMMENTS] delete error', err);
    }
  };

  const startEdit = (cmt) => {
    setEditingId(cmt.id);
    // Bereinigt laden: Inhalte aus API/MCP stammen nicht zwingend aus CKEditor.
    // Alter Klartext bleibt unverändert, sonst würde er doppelt maskiert.
    const raw = cmt.content || '';
    setEditingText(looksLikeHtml(raw) ? sanitizeRichHtml(raw) : raw);
    setEditingInternal(!!cmt.is_internal);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
    setEditingInternal(false);
  };

  const saveEdit = async (id, parentId) => {
    if (htmlIsEmpty(editingText)) return;
    setSavingEdit(true);
    const before = new Set(
      comments.concat(...Object.values(repliesByComment))
        .find((c) => c.id === id)?.mentions || []
    );
    try {
      const res = await commentsAPI.update(id, {
        content: editingText.trim(),
        ...(isClient ? {} : { is_internal: editingInternal }),
      });
      // Nur die durch die Bearbeitung neu hinzugekommenen Erwähnungen melden.
      announceMentions({ mentions: (res.data?.mentions || []).filter((uid) => !before.has(uid)) });
      cancelEdit();
      if (parentId) { await Promise.all([load(), loadReplies(parentId)]); }
      else { await load(); }
    } catch (err) {
      console.error('[COMMENTS] edit error', err);
    } finally {
      setSavingEdit(false);
    }
  };

  /** Kommentar umhängen: intern <-> kundensichtbar. */
  const applyVisibility = async (cmt, internal, parentId) => {
    try {
      await commentsAPI.setVisibility(cmt.id, internal);
      setNotice({
        severity: internal ? 'success' : 'warning',
        text: internal
          ? 'Kommentar ist jetzt intern – der Kunde sieht ihn nicht mehr.'
          : 'Kommentar ist jetzt für den Kunden sichtbar.',
      });
      if (parentId) { await Promise.all([load(), loadReplies(parentId)]); }
      else { await load(); }
      // Bereits geladene Antwort-Threads mitziehen (Verstecken kaskadiert)
      if (!parentId && repliesByComment[cmt.id]) await loadReplies(cmt.id);
    } catch (err) {
      console.error('[COMMENTS] visibility error', err);
      setNotice({
        severity: 'error',
        text: err?.response?.data?.error || 'Sichtbarkeit konnte nicht geändert werden.',
      });
    }
  };

  const toggleVisibility = (cmt, parentId) => {
    // Freigeben macht Inhalt für den Kunden sichtbar -> Rückfrage.
    if (cmt.is_internal) setPublishTarget({ cmt, parentId });
    else applyVisibility(cmt, true, parentId);
  };

  const toggleReplies = async (comment) => {
    if (openReplyFor === comment.id) { setOpenReplyFor(null); return; }
    setOpenReplyFor(comment.id);
    setReplyText('');
    setReplyInternal(false);
    if (!repliesByComment[comment.id]) await loadReplies(comment.id);
  };

  const renderComment = (cmt, isReply = false, parentId = null) => {
    const mine = user && cmt.user && cmt.user.id === user.id;
    const internal = !!cmt.is_internal;
    // Umhängen darf der Autor (intern) und jeder Admin – auch fremde Kommentare.
    const canToggleVisibility = !isClient && (mine || isAdmin());
    return (
      <Box key={cmt.id} sx={{
        display: 'flex', gap: 1.5, mb: 1.5, ml: isReply ? 5 : 0,
        ...(internal && {
          bgcolor: 'rgba(255, 193, 7, 0.08)',
          borderRadius: 1, p: 1, pl: 1.5,
        }),
      }}>
        <Avatar sx={{
          width: 32, height: 32, fontSize: '0.85rem',
          bgcolor: internal ? 'warning.dark' : 'primary.main',
        }}>
          {initials(cmt.user)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {cmt.user?.full_name || cmt.user?.username || 'Unbekannt'}
            </Typography>
            <Typography variant="caption" color="text.secondary">{fmt(cmt.created_at)}</Typography>
            {internal && (
              <Tooltip title="Interne Notiz – für den Kunden nicht sichtbar">
                <Chip size="small" color="warning" variant="outlined" label="Intern"
                  icon={<LockIcon sx={{ fontSize: 13 }} />}
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />
              </Tooltip>
            )}
          </Box>
          {editingId === cmt.id ? (
            <Box sx={{ mt: 0.5 }}>
              <CommentEditor key={`edit-${cmt.id}`} initialValue={cmt.content}
                placeholder="Kommentar bearbeiten…" onChange={setEditingText} />
              {!isClient && (
                <InternalToggle checked={editingInternal} onChange={setEditingInternal}
                  disabled={savingEdit} />
              )}
              <MentionHint html={editingText} users={mentionUsers} notified={cmt.mentions}
                isInternal={isClient ? cmt.is_internal : editingInternal} />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button size="small" variant="contained" onClick={() => saveEdit(cmt.id, parentId)}
                  disabled={savingEdit || htmlIsEmpty(editingText)}>Speichern</Button>
                <Button size="small" onClick={cancelEdit} disabled={savingEdit}>Abbrechen</Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mt: 0.25, fontSize: '0.875rem', lineHeight: 1.55,
                  '& code': { fontFamily: 'monospace', bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontSize: '0.85em' },
                  '& pre': { fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, overflowX: 'auto' },
                  '& strong, & b': { fontWeight: 700 },
                  '& ul, & ol': { pl: 3, my: 0.5 },
                  '& li': { mb: 0.25 },
                  '& p': { my: 0.5 },
                  '& a': { color: 'primary.main', textDecoration: 'underline' },
                  '& blockquote': { borderLeft: '3px solid #ccc', pl: 1.5, color: 'text.secondary', my: 0.5 },
                  // Erkannte @Erwähnung – nicht erkannte bleiben normaler Text
                  '& .rotis-mention': {
                    color: 'primary.main', fontWeight: 600,
                    bgcolor: 'rgba(30, 58, 95, 0.08)',
                    px: 0.4, borderRadius: 0.5,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: highlightMentions(renderRichHtml(cmt.content), mentionUsers),
                }}
              />
              <NotifiedLine ids={cmt.mentions} users={mentionUsers} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                {!isReply && (
                  <Button size="small" startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
                    onClick={() => toggleReplies(cmt)} sx={{ textTransform: 'none', minWidth: 0 }}>
                    {cmt.reply_count > 0 ? `Antworten (${cmt.reply_count})` : 'Antworten'}
                  </Button>
                )}
                {canToggleVisibility && (
                  <Tooltip title={internal
                    ? 'Für den Kunden freigeben'
                    : 'Als intern markieren – der Kunde sieht ihn dann nicht mehr'}>
                    <IconButton size="small" onClick={() => toggleVisibility(cmt, parentId)}>
                      {internal
                        ? <LockOpenIcon sx={{ fontSize: 16 }} color="action" />
                        : <LockIcon sx={{ fontSize: 16 }} color="action" />}
                    </IconButton>
                  </Tooltip>
                )}
                {mine && (
                  <IconButton size="small" onClick={() => startEdit(cmt)} title="Bearbeiten">
                    <EditIcon sx={{ fontSize: 16 }} color="action" />
                  </IconButton>
                )}
                {mine && (
                  <IconButton size="small" onClick={() => remove(cmt.id, parentId)} title="Löschen">
                    <DeleteIcon sx={{ fontSize: 16 }} color="error" />
                  </IconButton>
                )}
              </Box>
            </>
          )}

          {/* Replies + reply box */}
          {!isReply && openReplyFor === cmt.id && (
            <Box sx={{ mt: 1 }}>
              {(repliesByComment[cmt.id] || []).map((r) => renderComment(r, true, cmt.id))}
              <Box sx={{ ml: 5, mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <CommentEditor key={`reply-${cmt.id}`} placeholder="Antworten…" onChange={setReplyText} />
                  <Button variant="contained" onClick={() => postReply(cmt)} disabled={htmlIsEmpty(replyText)}>
                    <SendIcon fontSize="small" />
                  </Button>
                </Box>
                {!isClient && (internal ? (
                  <Typography variant="caption" color="warning.dark"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <LockIcon sx={{ fontSize: 13 }} />
                    Antwort auf eine interne Notiz – ebenfalls intern.
                  </Typography>
                ) : (
                  <InternalToggle checked={replyInternal} onChange={setReplyInternal} />
                ))}
                <MentionHint html={replyText} users={mentionUsers} isInternal={replyInternal} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CommentIcon color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Kommentare{comments.length ? ` (${comments.length})` : ''}
        </Typography>
      </Box>

      {/* New comment */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Avatar sx={{
            width: 32, height: 32, fontSize: '0.85rem',
            bgcolor: newInternal ? 'warning.dark' : 'secondary.main',
          }}>
            {initials(user)}
          </Avatar>
          <CommentEditor key={`new-${editorReset}`} onChange={setNewComment} />
          <Button variant="contained" onClick={postComment} disabled={posting || htmlIsEmpty(newComment)}
            color={newInternal ? 'warning' : 'primary'}
            sx={{ minWidth: 48 }}>
            {posting ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
          </Button>
        </Box>
        <Box sx={{ ml: 5 }}>
          {!isClient && (
            <InternalToggle checked={newInternal} onChange={setNewInternal} disabled={posting} />
          )}
          <MentionHint html={newComment} users={mentionUsers} isInternal={newInternal} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
      ) : comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Noch keine Kommentare. Stelle die erste Frage oder hinterlasse einen Hinweis.
        </Typography>
      ) : (
        comments.map((c) => renderComment(c))
      )}

      {/* Freigabe an den Kunden ist nicht zurückholbar (er kann sie schon gelesen haben) */}
      <Dialog open={!!publishTarget} onClose={() => setPublishTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Für den Kunden freigeben?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Dieser interne Kommentar wird damit für alle Kunden-Accounts sichtbar.
            Prüfe, ob er technische Details enthält, die der Kunde nicht sehen soll.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="warning" onClick={() => {
            const t = publishTarget;
            setPublishTarget(null);
            applyVisibility(t.cmt, false, t.parentId);
          }}>Freigeben</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!notice} autoHideDuration={4000} onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={notice?.severity || 'info'} variant="filled"
          onClose={() => setNotice(null)} sx={{ width: '100%' }}>
          {notice?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TaskComments;
